import {
  createTagSchema,
  NOTE_VISIBILITIES,
  NOTE_VISIBILITY_LABELS,
  TAG_COLORS,
  type CreateTagFormInput,
  type CreateTagInput,
  type NoteVisibility,
} from '@church/domain'
import {
  useCreateTag,
  useDeleteTag,
  useNoteCategories,
  useNoteCategoryMutations,
  useAccess,
  useTags,
  useUpdateTag,
} from '@church/data'
import {
  faCheck,
  faFloppyDisk,
  faNoteSticky,
  faPlus,
  faTags,
  faTrashCan,
  faUsers,
} from '@church/ui-icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { createFileRoute } from '@tanstack/react-router'
import { useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  ListCard,
  ListRow,
  ListSection,
  NewButton,
  usePanel,
} from '../components/platform/SettingsList.tsx'
import {
  Button,
  Card,
  Chip,
  ConfirmButton,
  EmptyState,
  ErrorNote,
  Field,
  Icon,
  Input,
  PanelSection,
  SidePanel,
  Spinner,
} from '../components/ui.tsx'
import { swatch } from '../components/tag-swatch.ts'

/**
 * Tags and note categories — the two taxonomies the People module hangs off.
 * They live in settings rather than on the profile because both are org-wide
 * decisions: a stray tag pollutes every picker, and a category's visibility
 * decides who can read the notes already filed in it.
 */
export const Route = createFileRoute('/_authed/_shell/settings/tags')({
  component: TagsAndCategoriesTab,
})

function TagsAndCategoriesTab() {
  return (
    <div className="max-w-2xl space-y-10">
      <TagsSection />
      <CategoriesSection />
    </div>
  )
}

// ---- tags -------------------------------------------------------------------

const people = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`

function TagsSection() {
  const { data: tags, isPending } = useTags()
  const access = useAccess()
  const panel = usePanel<string>()
  const tag = tags?.find((t) => t.id === panel.shown)

  return (
    <ListSection
      title="Tags"
      icon={faTags}
      action={
        access.has('people', 'editor') && (
          <NewButton label="New tag" onClick={() => panel.show('new')} />
        )
      }
    >
      {isPending ? (
        <Spinner />
      ) : !tags || tags.length === 0 ? (
        <Card>
          <EmptyState
            title="No tags yet"
            hint="Tags are your own labels: “Baptized 2026”, “Needs a ride”, “Choir”."
          />
        </Card>
      ) : (
        <ListCard>
          {tags.map((t) => (
            <ListRow
              key={t.id}
              leading={
                <span aria-hidden className={`size-3 shrink-0 rounded-full ${swatch(t.color)}`} />
              }
              title={t.name}
              meta={t.description ?? undefined}
              aside={
                <Chip>
                  <Icon icon={faUsers} className="size-3" />
                  {t.personCount}
                </Chip>
              }
              onOpen={access.has('people', 'editor') ? () => panel.show(t.id) : undefined}
            />
          ))}
        </ListCard>
      )}

      <TagPanel
        open={panel.open && (panel.shown === 'new' || tag !== undefined)}
        onOpenChange={panel.onOpenChange}
        onDone={panel.close}
        tag={panel.shown === 'new' ? undefined : tag}
      />
    </ListSection>
  )
}

type Tag = NonNullable<ReturnType<typeof useTags>['data']>[number]

function TagPanel({
  open,
  onOpenChange,
  onDone,
  tag,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDone: () => void
  tag: Tag | undefined
}) {
  const remove = useDeleteTag()
  const create = useCreateTag()
  const update = useUpdateTag(tag?.id ?? '')
  const mutation = tag ? update : create
  const formId = useId()
  const access = useAccess()
  return (
    <SidePanel
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          create.reset()
          update.reset()
        }
        onOpenChange(next)
      }}
      title={tag ? tag.name : 'New tag'}
      description={tag ? `On ${people(tag.personCount)}` : undefined}
      primary={
        <Button
          type="submit"
          form={formId}
          icon={tag ? faFloppyDisk : faPlus}
          className="py-1.5"
          pending={mutation.isPending}
          pendingLabel="Saving…"
        >
          {tag ? 'Save' : 'Add'}
        </Button>
      }
      footer={
        tag && access.has('people', 'administrator') ? (
          <ConfirmButton
            label="Delete tag"
            icon={faTrashCan}
            variant="quietDanger"
            className="-ml-4"
            disabled={remove.isPending}
            title={`Delete “${tag.name}”?`}
            description={`It will be removed from ${people(tag.personCount)}.`}
            confirmLabel="Delete tag"
            onConfirm={() => remove.mutate(tag.id, { onSuccess: onDone })}
          />
        ) : undefined
      }
    >
      <TagForm
        key={tag?.id ?? 'new'}
        formId={formId}
        tagId={tag?.id}
        create={create}
        update={update}
        defaults={
          tag
            ? {
                name: tag.name,
                color: (tag.color ?? null) as CreateTagFormInput['color'],
                description: tag.description,
              }
            : undefined
        }
        onDone={onDone}
      />
      <ErrorNote error={remove.error} />
    </SidePanel>
  )
}

function TagForm({
  formId,
  tagId,
  create,
  update,
  defaults,
  onDone,
}: {
  formId: string
  tagId?: string
  create: ReturnType<typeof useCreateTag>
  update: ReturnType<typeof useUpdateTag>
  defaults?: CreateTagFormInput
  onDone: () => void
}) {
  const mutation = tagId ? update : create

  const form = useForm<CreateTagFormInput, unknown, CreateTagInput>({
    resolver: zodResolver(createTagSchema),
    defaultValues: defaults ?? { color: 'slate' },
  })
  const color = form.watch('color')

  const submit = form.handleSubmit(async (input) => {
    if (tagId) await update.mutateAsync(input)
    else await create.mutateAsync(input)
    form.reset(tagId ? undefined : { color: 'slate' })
    onDone()
  })

  return (
    <form id={formId} onSubmit={(e) => void submit(e)} className="space-y-5" noValidate>
      <Field label="Name" error={form.formState.errors.name?.message}>
        <Input placeholder="Baptized 2026" {...form.register('name')} />
      </Field>
      <PanelSection title="Colour">
        <div className="flex flex-wrap gap-2">
          {TAG_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-pressed={color === c}
              onClick={() => form.setValue('color', c, { shouldDirty: true })}
              className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm capitalize ${
                color === c
                  ? 'border-brand-solid bg-brand-soft text-content-brand'
                  : 'border-border-default text-content-default hover:bg-surface-sunken'
              }`}
            >
              <span aria-hidden className={`size-3 rounded-full ${swatch(c)}`} />
              {c}
              {color === c && <Icon icon={faCheck} className="size-3" />}
            </button>
          ))}
        </div>
      </PanelSection>
      <Field label="Description" error={form.formState.errors.description?.message}>
        <Input placeholder="Optional" {...form.register('description')} />
      </Field>

      <ErrorNote error={mutation.error} />
    </form>
  )
}

// ---- note categories ---------------------------------------------------------

const visibilityLabel = (permission: string) =>
  NOTE_VISIBILITY_LABELS[permission as NoteVisibility] ?? permission

function CategoriesSection() {
  const { data: categories, isPending } = useNoteCategories()
  const access = useAccess()
  const panel = usePanel<string>()
  const category = categories?.find((c) => c.id === panel.shown)

  return (
    <ListSection
      title="Note categories"
      icon={faNoteSticky}
      hint="A category’s visibility decides who can read every note filed in it — including notes filed before the change."
      action={
        access.has('people', 'administrator', { orgWide: true }) && (
          <NewButton label="New category" onClick={() => panel.show('new')} />
        )
      }
    >
      {isPending ? (
        <Spinner />
      ) : !categories || categories.length === 0 ? (
        <Card>
          <EmptyState
            title="No note categories yet"
            hint="Notes are filed in a category: “Pastoral care”, “Follow-up”."
          />
        </Card>
      ) : (
        <ListCard>
          {categories.map((c) => (
            <ListRow
              key={c.id}
              title={c.name}
              meta={visibilityLabel(c.requiredPermission)}
              aside={c.isDefault ? <Chip>Default</Chip> : undefined}
              onOpen={
                access.has('people', 'administrator', { orgWide: true })
                  ? () => panel.show(c.id)
                  : undefined
              }
            />
          ))}
        </ListCard>
      )}

      <CategoryPanel
        open={panel.open && (panel.shown === 'new' || category !== undefined)}
        onOpenChange={panel.onOpenChange}
        onDone={panel.close}
        category={panel.shown === 'new' ? undefined : category}
      />
    </ListSection>
  )
}

type NoteCategory = NonNullable<ReturnType<typeof useNoteCategories>['data']>[number]

function CategoryPanel({
  open,
  onOpenChange,
  onDone,
  category,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onDone: () => void
  category: NoteCategory | undefined
}) {
  const { create, update, remove } = useNoteCategoryMutations()
  const [name, setName] = useState(category?.name ?? '')
  const [visibility, setVisibility] = useState<NoteVisibility>(
    (category?.requiredPermission as NoteVisibility | undefined) ?? 'people:read',
  )
  // A fresh form each time it opens, from the category as it is now (it stays
  // mounted, so it can slide out).
  const openFor = open ? (category?.id ?? 'new') : null
  const [openedFor, setOpenedFor] = useState(openFor)
  if (openFor !== openedFor) {
    setOpenedFor(openFor)
    if (openFor !== null) {
      setName(category?.name ?? '')
      setVisibility((category?.requiredPermission as NoteVisibility | undefined) ?? 'people:read')
    }
  }
  const save = () => {
    const body = { name: name.trim(), requiredPermission: visibility }
    if (category) update.mutate({ categoryId: category.id, ...body }, { onSuccess: onDone })
    else create.mutate(body, { onSuccess: onDone })
  }
  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={category ? category.name : 'New note category'}
      description={category?.isDefault ? 'The default category' : undefined}
      footer={
        category && !category.isDefault ? (
          <ConfirmButton
            label="Delete category"
            icon={faTrashCan}
            variant="quietDanger"
            className="-ml-4"
            disabled={remove.isPending}
            title={`Delete “${category.name}”?`}
            description="Notes already filed here must be moved or deleted first."
            confirmLabel="Delete category"
            onConfirm={() => remove.mutate(category.id, { onSuccess: onDone })}
          />
        ) : undefined
      }
      primary={
        <Button
          icon={category ? faFloppyDisk : faPlus}
          className="py-1.5"
          pending={create.isPending || update.isPending}
          pendingLabel="Saving…"
          disabled={name.trim() === ''}
          onClick={save}
        >
          {category ? 'Save' : 'Add'}
        </Button>
      }
    >
      <Field label="Name">
        <Input placeholder="Pastoral care" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <PanelSection
        title="Who can read its notes"
        hint="Changing this changes who can read the notes already filed here."
      >
        <div className="space-y-1.5">
          {NOTE_VISIBILITIES.map((v) => (
            <label
              key={v}
              className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 text-sm ${
                visibility === v
                  ? 'border-brand-solid bg-brand-soft text-content-default'
                  : 'border-border-default text-content-default hover:bg-surface-sunken'
              }`}
            >
              <input
                type="radio"
                name="visibility"
                className="accent-brand-solid"
                checked={visibility === v}
                onChange={() => setVisibility(v)}
              />
              {NOTE_VISIBILITY_LABELS[v]}
            </label>
          ))}
        </div>
      </PanelSection>
      <ErrorNote error={create.error ?? update.error ?? remove.error} />
    </SidePanel>
  )
}
